import { useEffect, useState } from 'react'
import { Plus, X } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select'
import { useNotify } from '@/components/shared/useNotify'
import {
  VENDOR_KINDS,
  type VendorKind,
  type AdminVendorDetail,
  useAdminVendor,
  useEditVendor,
  useAddAlias,
  useRemoveAlias,
} from '@/hooks/useAdminVendors'

interface AdminVendorEditDialogProps {
  vendorId: string | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

interface FormState {
  name: string
  slug: string
  vendorKind: VendorKind
  vendorTags: string[]
  countryIso: string
  description: string
  homepageUrl: string
  notes: string
}

function vendorToForm(v: AdminVendorDetail): FormState {
  return {
    name: v.name,
    slug: v.slug,
    vendorKind: v.vendorKind,
    vendorTags: v.vendorTags ?? [],
    countryIso: v.countryIso ?? '',
    description: v.description ?? '',
    homepageUrl: v.homepageUrl ?? '',
    notes: v.notes ?? '',
  }
}

/** Diff form against original vendor and produce a PATCH body (only-changed). */
function buildPatchBody(form: FormState, original: AdminVendorDetail) {
  const body: Record<string, unknown> = {}
  if (form.name !== original.name) body.name = form.name
  if (form.slug !== original.slug) body.slug = form.slug
  if (form.vendorKind !== original.vendorKind) body.vendorKind = form.vendorKind
  const origTags = original.vendorTags ?? []
  if (
    form.vendorTags.length !== origTags.length ||
    form.vendorTags.some((t, i) => t !== origTags[i])
  ) {
    body.vendorTags = form.vendorTags
  }
  if ((form.countryIso || null) !== (original.countryIso || null))
    body.countryIso = form.countryIso || null
  if ((form.description || null) !== (original.description || null))
    body.description = form.description || null
  if ((form.homepageUrl || null) !== (original.homepageUrl || null))
    body.homepageUrl = form.homepageUrl || null
  if ((form.notes || null) !== (original.notes || null))
    body.notes = form.notes || null
  return body
}

export function AdminVendorEditDialog({
  vendorId,
  open,
  onOpenChange,
}: AdminVendorEditDialogProps) {
  const detailQuery = useAdminVendor(open && vendorId ? vendorId : undefined)
  const edit = useEditVendor(vendorId ?? '')
  const addAlias = useAddAlias()
  const removeAlias = useRemoveAlias()
  const { notify } = useNotify()

  const [form, setForm] = useState<FormState | null>(null)
  const [tagInput, setTagInput] = useState('')
  const [aliasInput, setAliasInput] = useState('')

  const detail = detailQuery.data
  // Hydrate form when detail arrives.
  useEffect(() => {
    if (detail && form === null) {
      setForm(vendorToForm(detail))
    }
    if (!open) {
      setForm(null)
      setTagInput('')
      setAliasInput('')
    }
  }, [detail, form, open])

  function set<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev))
  }

  function addTag() {
    const trimmed = tagInput.trim()
    if (!trimmed || !form) return
    if (form.vendorTags.includes(trimmed)) {
      setTagInput('')
      return
    }
    set('vendorTags', [...form.vendorTags, trimmed])
    setTagInput('')
  }

  function removeTag(tag: string) {
    if (!form) return
    set(
      'vendorTags',
      form.vendorTags.filter((t) => t !== tag),
    )
  }

  async function handleAddAlias() {
    const trimmed = aliasInput.trim()
    if (!trimmed || !vendorId) return
    try {
      await addAlias.mutateAsync({ vendorId, alias: trimmed })
      setAliasInput('')
      notify({ title: `Alias “${trimmed}” added`, variant: 'success' })
    } catch {
      notify({ title: 'Failed to add alias', variant: 'error' })
    }
  }

  async function handleRemoveAlias(aliasId: string, label: string) {
    if (!vendorId) return
    try {
      await removeAlias.mutateAsync({ vendorId, aliasId })
      notify({ title: `Alias “${label}” removed`, variant: 'success' })
    } catch {
      notify({ title: 'Failed to remove alias', variant: 'error' })
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!form || !detail || !vendorId) return
    const body = buildPatchBody(form, detail)
    if (Object.keys(body).length === 0) {
      onOpenChange(false)
      return
    }
    try {
      await edit.mutateAsync(body as Parameters<typeof edit.mutateAsync>[0])
      notify({ title: `${form.name} saved`, variant: 'success' })
      onOpenChange(false)
    } catch (err) {
      notify({
        title: 'Save failed',
        description: (err as Error).message,
        variant: 'error',
      })
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="vendor-edit-dialog"
      >
        <DialogHeader>
          <DialogTitle>Edit Vendor</DialogTitle>
          <DialogDescription>
            Update vendor metadata + manage display/canonical aliases.
          </DialogDescription>
        </DialogHeader>

        {detailQuery.isLoading || !form ? (
          <p className="text-sm text-muted-foreground py-6">Loading…</p>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="vendor-name">Name *</Label>
                <Input
                  id="vendor-name"
                  required
                  value={form.name}
                  onChange={(e) => set('name', e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="vendor-slug">Slug *</Label>
                <Input
                  id="vendor-slug"
                  required
                  value={form.slug}
                  onChange={(e) => set('slug', e.target.value)}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="vendor-kind">Kind</Label>
                <Select
                  value={form.vendorKind}
                  onValueChange={(v) => set('vendorKind', v as VendorKind)}
                >
                  <SelectTrigger id="vendor-kind">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {VENDOR_KINDS.map((k) => (
                      <SelectItem key={k} value={k}>
                        {k}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="vendor-country">Country (ISO α2)</Label>
                <Input
                  id="vendor-country"
                  placeholder="us"
                  maxLength={2}
                  value={form.countryIso}
                  onChange={(e) =>
                    set('countryIso', e.target.value.toLowerCase())
                  }
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>Tags</Label>
              <div className="flex flex-wrap gap-1.5 min-h-[2rem]">
                {form.vendorTags.map((tag) => (
                  <Badge
                    key={tag}
                    variant="secondary"
                    className="gap-1 pr-1"
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => removeTag(tag)}
                      className="rounded-sm hover:bg-muted"
                      aria-label={`Remove tag ${tag}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
              <div className="flex gap-2">
                <Input
                  placeholder="Add tag…"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      addTag()
                    }
                  }}
                />
                <Button type="button" variant="outline" size="sm" onClick={addTag}>
                  <Plus className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>Aliases</Label>
              <p className="text-xs text-muted-foreground">
                Aliases are managed independently of the main save (separate
                POST/DELETE per Task 16).
              </p>
              <div className="flex flex-wrap gap-1.5 min-h-[2rem]">
                {detail?.aliases.map((a) => (
                  <Badge
                    key={a.id}
                    variant={a.source === 'canonical' ? 'outline' : 'secondary'}
                    className="gap-1 pr-1"
                    title={`source: ${a.source}`}
                  >
                    <span>{a.alias}</span>
                    {a.source !== 'canonical' && (
                      <button
                        type="button"
                        onClick={() => handleRemoveAlias(a.id, a.alias)}
                        className="rounded-sm hover:bg-muted"
                        aria-label={`Remove alias ${a.alias}`}
                        disabled={removeAlias.isPending}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    )}
                  </Badge>
                ))}
              </div>
              <div className="flex gap-2">
                <Input
                  placeholder="Add alias…"
                  value={aliasInput}
                  onChange={(e) => setAliasInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      void handleAddAlias()
                    }
                  }}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => void handleAddAlias()}
                  disabled={addAlias.isPending}
                >
                  <Plus className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="vendor-description">Description</Label>
              <Textarea
                id="vendor-description"
                rows={2}
                value={form.description}
                onChange={(e) => set('description', e.target.value)}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="vendor-homepage">Homepage URL</Label>
              <Input
                id="vendor-homepage"
                type="url"
                placeholder="https://vendor.example"
                value={form.homepageUrl}
                onChange={(e) => set('homepageUrl', e.target.value)}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="vendor-notes">Notes</Label>
              <Textarea
                id="vendor-notes"
                rows={2}
                value={form.notes}
                onChange={(e) => set('notes', e.target.value)}
              />
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={edit.isPending}>
                {edit.isPending ? 'Saving…' : 'Save'}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  )
}
